import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { WhopCheckoutEmbed } from "@whop/checkout/react";

const API = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : "/api";

const LOGO = "/deck/assets/a-001-1c393ab857cb.png";
const SESSION_KEY = "skye_onboard_session_id";

// URL slug → backend offer key
const SLUG_TO_OFFER = {
  "pay-full": "single_pay", "pay-in-full": "single_pay", "full": "single_pay", "single": "single_pay",
  "split-2": "split_2", "split2": "split_2", "split-in-2": "split_2",
  "split-3": "split_3", "split3": "split_3", "split-in-3": "split_3",
};

// Progressive US phone formatter — turns raw digits into (XXX) XXX-XXXX as they type.
// Handles paste, backspace, and leading +1. Non-US numbers with >10 digits get spaced.
function formatPhone(raw) {
  if (!raw) return "";
  let d = String(raw).replace(/\D/g, "");
  if (d.length === 11 && d.startsWith("1")) d = d.slice(1); // strip US country code
  if (d.length === 0) return "";
  if (d.length <= 3) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  if (d.length <= 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  // >10 digits — likely intl, keep as spaced group
  return `+${d.slice(0, d.length - 10)} (${d.slice(-10, -7)}) ${d.slice(-7, -4)}-${d.slice(-4)}`;
}

/**
 * /onboard — post-sale 3-step wizard (Pay-Inline → Calendar → Business Info)
 * Per-offer URLs · inline Whop embed · zero-redirect card entry.
 */
export default function OnboardWizard() {
  const [session, setSession] = useState(null);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  const offerFromUrl = useMemo(() => {
    try {
      const parts = window.location.pathname.split("/").filter(Boolean);
      return SLUG_TO_OFFER[(parts[1] || "").toLowerCase()] || null;
    } catch { return null; }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const cfg = await axios.get(`${API}/onboard/config`);
        setConfig(cfg.data);
        const savedId = localStorage.getItem(SESSION_KEY);
        if (savedId) {
          try {
            const res = await axios.get(`${API}/onboard/session/${savedId}`);
            if (res.data.current_step !== "complete") setSession(res.data);
            else localStorage.removeItem(SESSION_KEY);
          } catch { localStorage.removeItem(SESSION_KEY); }
        }
      } catch {}
      setLoading(false);
    })();
  }, []);

  const refreshSession = useCallback(async () => {
    if (!session?.session_id) return;
    try {
      const res = await axios.get(`${API}/onboard/session/${session.session_id}`);
      setSession(res.data);
    } catch {}
  }, [session?.session_id]);

  const startSession = useCallback(async (email, first_name, last_name, phone, business_name) => {
    const res = await axios.post(`${API}/onboard/session`, { email, first_name, last_name, phone, business_name });
    setSession(res.data);
    localStorage.setItem(SESSION_KEY, res.data.session_id);
    return res.data;
  }, []);

  const reset = useCallback(() => {
    localStorage.removeItem(SESSION_KEY);
    setSession(null);
  }, []);

  if (loading) return <div style={S.loading}><div style={S.spinner} /></div>;
  const inPayStep = session && session.current_step === "payment";

  return (
    <div style={S.page}>
      <div style={S.bgGlow} />
      <header style={S.header}>
        <img src={LOGO} alt="Skye AI" style={S.logo} />
        <span style={S.badge}>ONBOARDING</span>
      </header>
      <main style={S.main}>
        {!session && !offerFromUrl ? (
          <MissingOfferScreen />
        ) : session && session.current_step === "complete" ? (
          <CompleteStep session={session} onReset={reset} />
        ) : (
          <>
            <ProgressBar current={session ? session.current_step : "payment"} />
            {(!session || inPayStep) && (
              <InfoAndPayStep
                config={config}
                offerFromUrl={offerFromUrl}
                existingSession={inPayStep ? session : null}
                onStart={startSession}
                onRefresh={refreshSession}
              />
            )}
            {session && session.current_step === "booking" && <CalendarStep session={session} onRefresh={refreshSession} />}
            {session && session.current_step === "intake" && <IntakeStep session={session} />}
          </>
        )}
      </main>
      <footer style={S.footer}>Need help? Text your rep · replies within minutes</footer>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// STEP 1 · Info fields + inline Whop card embed (same page)
// ═══════════════════════════════════════════════════════════════════════
function InfoAndPayStep({ config, offerFromUrl, existingSession, onStart, onRefresh }) {
  const [email, setEmail] = useState(existingSession?.email || "");
  const [firstName, setFirstName] = useState(existingSession?.first_name || "");
  const [lastName, setLastName] = useState(existingSession?.last_name || "");
  const [phone, setPhone] = useState(() => formatPhone(existingSession?.phone || ""));
  const [businessName, setBusinessName] = useState(existingSession?.business_name || "");
  const [chosenOffer, setChosenOffer] = useState(offerFromUrl);
  const [busy, setBusy] = useState(false);
  const [checkoutSessionId, setCheckoutSessionId] = useState(null);
  const [externalUrl, setExternalUrl] = useState(null);
  const [localError, setLocalError] = useState("");
  const pollRef = useRef(null);
  const autoInitRef = useRef(false);

  // Prefill from URL query params
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      const e = p.get("email"); if (e && !email) setEmail(e);
      const f = p.get("first_name") || p.get("first"); if (f && !firstName) setFirstName(f);
      const l = p.get("last_name") || p.get("last"); if (l && !lastName) setLastName(l);
      const ph = p.get("phone"); if (ph && !phone) setPhone(formatPhone(ph));
      const bn = p.get("business_name") || p.get("business"); if (bn && !businessName) setBusinessName(bn);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll session status once user has been sent to embed / external
  useEffect(() => {
    if (!checkoutSessionId && !externalUrl) return;
    pollRef.current = setInterval(onRefresh, 3000);
    return () => clearInterval(pollRef.current);
  }, [checkoutSessionId, externalUrl, onRefresh]);

  const offers = config?.offers || {};
  const chosen = chosenOffer ? offers[chosenOffer] : null;

  // Required-field validation — all 5 must be filled and email/phone must look valid
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const phoneDigits = (phone || "").replace(/\D/g, "");
  const phoneValid = phoneDigits.length >= 10;
  const infoComplete = emailValid && firstName.trim() && lastName.trim() && phoneValid && businessName.trim();
  const canProceed = infoComplete && chosenOffer && offers[chosenOffer]?.available;

  const initEmbed = useCallback(async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!canProceed || busy || checkoutSessionId || externalUrl) return;
    setBusy(true);
    setLocalError("");
    try {
      const sess = existingSession || await onStart(email.trim(), firstName.trim(), lastName.trim(), phone.trim(), businessName.trim());
      const co = await axios.post(`${API}/onboard/checkout-session`, {
        session_id: sess.session_id, offer: chosenOffer,
      });
      if (co.data.external) {
        setExternalUrl(co.data.external_url);
        window.open(co.data.external_url, "_blank", "noopener");
      } else {
        setCheckoutSessionId(co.data.checkout_session_id);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || "Could not start checkout. Try again.";
      setLocalError(typeof msg === "string" ? msg : "Something went wrong.");
      autoInitRef.current = false; // allow retry when they fix the fields
    }
    setBusy(false);
  }, [canProceed, busy, checkoutSessionId, externalUrl, existingSession, onStart, email, firstName, lastName, phone, businessName, chosenOffer]);

  // Auto-init card entry the moment ALL 5 required fields are valid.
  // We flip `autoInitRef` INSIDE the timer (not before) so React re-renders
  // during the debounce window don't lose the schedule. And we set a
  // failure flag on error so the retry doesn't infinite-loop.
  const autoInitTimerRef = useRef(null);
  useEffect(() => {
    if (autoInitRef.current) return; // already fired
    if (!canProceed || checkoutSessionId || externalUrl || busy || localError) return;
    if (autoInitTimerRef.current) clearTimeout(autoInitTimerRef.current);
    autoInitTimerRef.current = setTimeout(() => {
      autoInitTimerRef.current = null;
      autoInitRef.current = true;
      initEmbed();
    }, 400);
    return () => {
      if (autoInitTimerRef.current) {
        clearTimeout(autoInitTimerRef.current);
        autoInitTimerRef.current = null;
      }
    };
  }, [canProceed, checkoutSessionId, externalUrl, busy, localError, initEmbed]);

  const confirmPayFull = async () => {
    if (!existingSession?.session_id) return;
    try {
      await axios.post(`${API}/onboard/payfull-confirm`, { session_id: existingSession.session_id });
      await onRefresh();
    } catch { setLocalError("Could not confirm payment. Try again."); }
  };

  // ── PENDING · External pay-in-full ────────────────────────────────
  if (externalUrl) {
    return (
      <div style={S.card} data-testid="onboard-external-pending">
        <div style={S.eyebrow}>{chosen?.label}</div>
        <h2 style={S.h2}>Complete payment in the new tab</h2>
        <p style={S.sub}>Once you finish, come back here and hit the button below.</p>
        <div style={S.pendingBox}>
          <div style={S.pendingSpinner} />
          <div>
            <div style={S.pendingTitle}>Waiting for payment…</div>
            <div style={S.pendingSub}>Or tap the button once you&rsquo;re done.</div>
          </div>
        </div>
        <a href={externalUrl} target="_blank" rel="noopener" style={S.secondaryBtn}>Open payment tab again</a>
        <button onClick={confirmPayFull} style={S.primaryBtn} data-testid="payfull-confirm-btn">I paid, continue →</button>
      </div>
    );
  }

  // ── FORM · Info fields (always visible) + inline card embed below ──
  const returnUrl = `${window.location.origin}${window.location.pathname}?paid=1`;
  return (
    <form style={S.card} onSubmit={initEmbed} data-testid="onboard-info-pay-form">
      <div style={S.eyebrow}>Step 1 · Card + your info</div>
      <h1 style={S.h1}>{firstName ? `Welcome, ${firstName}` : "Welcome to Skye"}</h1>
      <p style={S.sub}>Card details first — then book your kickoff call — then a quick business form. About 3 minutes total.</p>

      {/* Here's what happens next — makes the order obvious */}
      <div style={S.flowPreview} data-testid="onboard-flow-preview">
        <div style={S.flowStep}>
          <span style={{ ...S.flowNum, ...S.flowNumActive }}>1</span>
          <span style={S.flowStepLabel}>Card + info<span style={S.flowStepNow}>· now</span></span>
        </div>
        <span style={S.flowArrow}>→</span>
        <div style={S.flowStep}>
          <span style={S.flowNum}>2</span>
          <span style={S.flowStepLabel}>Book kickoff</span>
        </div>
        <span style={S.flowArrow}>→</span>
        <div style={S.flowStep}>
          <span style={S.flowNum}>3</span>
          <span style={S.flowStepLabel}>Business form</span>
        </div>
      </div>

      {/* Offer summary — URL is the single source of truth; total always shown */}
      {chosen && (
        <div style={S.lockedOffer} data-testid="onboard-locked-offer">
          <div style={S.lockedOfferLabel}>{chosen.label}</div>
          <div style={S.lockedOfferPrice}>
            <b>${chosen.amount.toLocaleString()}</b>
            <span>{chosen.count ? ` today · then ${chosen.count - 1} more monthly` : " one-time"}</span>
          </div>
          <div style={S.lockedOfferTotalRow}>
            <span style={S.lockedOfferTotalLabel}>Total</span>
            <span style={S.lockedOfferTotalValue} data-testid="onboard-locked-total">
              ${(chosen.count ? chosen.amount * chosen.count : chosen.amount).toLocaleString()}
            </span>
          </div>
          {chosen.count && (
            <div style={S.lockedOfferFinePrint}>
              {chosen.count} payments of ${chosen.amount.toLocaleString()} · 30 days apart
            </div>
          )}
        </div>
      )}

      {/* ─── Your info section ─── */}
      <div style={S.sectionLabel}>Your info</div>
      <div style={{ ...S.field, marginTop: 4 }}>
        <label style={S.label}>Your email</label>
        <input type="email" required autoFocus inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@yourbusiness.com" style={S.input} disabled={!!checkoutSessionId} data-testid="onboard-email-input" />
      </div>
      <div style={S.row}>
        <div style={{ ...S.field, flex: 1 }}>
          <label style={S.label}>First name</label>
          <input required value={firstName} onChange={(e) => setFirstName(e.target.value)} placeholder="First" style={S.input} disabled={!!checkoutSessionId} data-testid="onboard-first-input" />
        </div>
        <div style={{ ...S.field, flex: 1 }}>
          <label style={S.label}>Last name</label>
          <input required value={lastName} onChange={(e) => setLastName(e.target.value)} placeholder="Last" style={S.input} disabled={!!checkoutSessionId} data-testid="onboard-last-input" />
        </div>
      </div>
      <div style={S.field}>
        <label style={S.label}>Phone</label>
        <input type="tel" required inputMode="tel" value={phone} onChange={(e) => setPhone(formatPhone(e.target.value))} placeholder="(555) 555-5555" style={S.input} disabled={!!checkoutSessionId} maxLength={16} data-testid="onboard-phone-input" />
      </div>
      <div style={S.field}>
        <label style={S.label}>Business name</label>
        <input required value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="Your business" style={S.input} disabled={!!checkoutSessionId} data-testid="onboard-business-input" />
      </div>

      {localError && <div style={S.error}>{localError}</div>}

      {/* ─── Card entry section — auto-loads once info above is complete ─── */}
      <div style={{ ...S.sectionLabel, marginTop: 18 }}>Card details</div>

      {!checkoutSessionId ? (
        <>
          <div style={S.cardHint} data-testid="onboard-card-hint">
            <span style={S.cardHintIcon}>{infoComplete && busy ? "⏳" : infoComplete ? "🔒" : "✍️"}</span>
            <span>
              {infoComplete && busy
                ? "Loading secure card entry…"
                : infoComplete
                  ? "All set — card entry loading below"
                  : "Fill in the 5 fields above and secure card entry appears here automatically"}
            </span>
          </div>
          {!infoComplete && (
            <div style={S.trustRow}>
              <span>🔒 Encrypted by Whop</span>
              <span>·</span>
              <span>Apple Pay · Google Pay · Card</span>
            </div>
          )}
        </>
      ) : (
        <div style={S.embedBlock} data-testid="onboard-embed-block">
          <div style={S.embedTop}>
            <div style={S.embedTopTitle}>Enter your card below</div>
            <div style={S.embedTopSub}>Card, Apple Pay, Google Pay, Cash App · you never leave this page</div>
          </div>
          <div style={S.embedWrap}>
            <WhopCheckoutEmbed
              sessionId={checkoutSessionId}
              theme="dark"
              setupFutureUsage="off_session"
              prefill={{ email }}
              returnUrl={returnUrl}
              onComplete={() => { onRefresh(); }}
              onPaymentError={(evt) => {
                console.error("Whop payment error", evt);
                setLocalError("Payment failed. Check the details and try again.");
              }}
              fallback={<div style={S.embedLoading}><div style={S.pendingSpinner} /><span style={{marginLeft:12}}>Loading secure checkout…</span></div>}
            />
          </div>
          <div style={S.pendingHintRow}>
            <span style={S.pendingSpinnerSmall} />
            <span>Waiting for payment · we&rsquo;ll unlock booking the moment it clears</span>
          </div>
        </div>
      )}
    </form>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// SCREEN · No offer slug in URL — hide the picker, ask them for their link
// ═══════════════════════════════════════════════════════════════════════
function MissingOfferScreen() {
  return (
    <div style={S.card} data-testid="onboard-missing-offer">
      <div style={S.eyebrow}>Onboarding</div>
      <h1 style={S.h1}>You need your personal link</h1>
      <p style={S.sub}>
        Your rep sent you a link with your payment plan built in. Open that link to continue —
        it&rsquo;ll look like <code style={S.inlineCode}>skyeai.io/onboard/…</code>
      </p>
      <div style={S.pendingBox}>
        <div style={S.pendingSpinner} />
        <div>
          <div style={S.pendingTitle}>Can&rsquo;t find it?</div>
          <div style={S.pendingSub}>Text your rep — they&rsquo;ll resend it in seconds.</div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// PROGRESS BAR
// ═══════════════════════════════════════════════════════════════════════
function ProgressBar({ current }) {
  const steps = [
    { id: "payment", label: "Card + Info", icon: "💳" },
    { id: "booking", label: "Book Kickoff", icon: "📅" },
    { id: "intake", label: "Business", icon: "📝" },
  ];
  const idx = steps.findIndex((s) => s.id === current);
  return (
    <div style={S.progress}>
      {steps.map((s, i) => (
        <div key={s.id} style={S.progressStep}>
          <div style={{
            ...S.progressDot,
            background: i <= idx ? "linear-gradient(135deg,#dc328c,#9d32f3)" : "rgba(255,255,255,.08)",
            color: i <= idx ? "#fff" : "rgba(255,255,255,.4)",
            boxShadow: i === idx ? "0 0 24px rgba(220,50,140,.55)" : "none",
          }}>{i < idx ? "✓" : s.icon}</div>
          <div style={{ ...S.progressLabel, color: i <= idx ? "#fff" : "rgba(255,255,255,.4)" }}>{s.label}</div>
          {i < steps.length - 1 && (
            <div style={{ ...S.progressLine, background: i < idx ? "linear-gradient(90deg,#dc328c,#9d32f3)" : "rgba(255,255,255,.08)" }} />
          )}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// STEP · CALENDAR
// ═══════════════════════════════════════════════════════════════════════
function CalendarStep({ session, onRefresh }) {
  const [availability, setAvailability] = useState(null);
  const [pickedDate, setPickedDate] = useState(null);
  const [pickedSlot, setPickedSlot] = useState(null);
  const [booking, setBooking] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/onboard/availability?days=14`);
        setAvailability(res.data);
        const dates = Object.keys(res.data.slots_by_date || {}).sort();
        if (dates[0]) setPickedDate(dates[0]);
      } catch { setErr("Could not load available times. Please refresh."); }
    })();
  }, []);

  const book = async () => {
    if (!pickedSlot || booking) return;
    setBooking(true);
    setErr("");
    try {
      await axios.post(`${API}/onboard/book`, { session_id: session.session_id, start_time: pickedSlot });
      await onRefresh();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not book that time. Try another.");
      setBooking(false);
    }
  };

  if (!availability) return <div style={S.card}><div style={S.pendingSpinner} /></div>;
  const dates = Object.keys(availability.slots_by_date || {}).sort();
  const slotsForDate = pickedDate ? (availability.slots_by_date[pickedDate] || []) : [];

  return (
    <div style={S.card} data-testid="onboard-calendar">
      <div style={S.eyebrow}>Step 2 · Book Kickoff Call</div>
      <h2 style={S.h2}>Pick a time that works</h2>
      <p style={S.sub}>30 minutes with our team · {availability.timezone} time</p>
      {dates.length === 0 ? (
        <div style={S.info}>No open times in the next 14 days. Your rep will reach out — check your email.</div>
      ) : (
        <>
          <div style={S.dateScroller}>
            {dates.map((d) => {
              const [y, m, day] = d.split("-");
              const dateObj = new Date(+y, +m - 1, +day);
              const weekday = dateObj.toLocaleDateString("en-US", { weekday: "short" });
              const monthDay = dateObj.toLocaleDateString("en-US", { month: "short", day: "numeric" });
              const active = d === pickedDate;
              return (
                <button key={d} type="button" onClick={() => { setPickedDate(d); setPickedSlot(null); }} style={{ ...S.dateChip, ...(active ? S.dateChipActive : {}) }} data-testid={`onboard-date-${d}`}>
                  <span style={S.dateChipDay}>{weekday}</span>
                  <span style={S.dateChipDate}>{monthDay}</span>
                  <span style={S.dateChipCount}>{(availability.slots_by_date[d] || []).length} open</span>
                </button>
              );
            })}
          </div>
          <div style={S.slotGrid}>
            {slotsForDate.length === 0 && <div style={S.info}>No open times that day. Try another date.</div>}
            {slotsForDate.map((s) => {
              const label = new Date(s).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
              const active = s === pickedSlot;
              return <button key={s} type="button" onClick={() => setPickedSlot(s)} style={{ ...S.slotBtn, ...(active ? S.slotBtnActive : {}) }} data-testid={`onboard-slot-${s}`}>{label}</button>;
            })}
          </div>
          {err && <div style={S.error}>{err}</div>}
          <button onClick={book} disabled={!pickedSlot || booking} style={{ ...S.primaryBtn, opacity: pickedSlot && !booking ? 1 : 0.4 }} data-testid="onboard-book-btn">
            {booking ? "Booking…" : pickedSlot
              ? `Book ${new Date(pickedSlot).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}`
              : "Pick a time above"}
          </button>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// STEP · INTAKE (redirects to /start)
// ═══════════════════════════════════════════════════════════════════════
function IntakeStep({ session }) {
  const startUrl = `/start?email=${encodeURIComponent(session.email)}&first_name=${encodeURIComponent(session.first_name || "")}&last_name=${encodeURIComponent(session.last_name || "")}&onboard=${session.session_id}`;
  const [t, setT] = useState(3);
  useEffect(() => {
    const i = setInterval(() => setT((v) => (v > 0 ? v - 1 : 0)), 1000);
    return () => clearInterval(i);
  }, []);
  useEffect(() => { if (t === 0) window.location.href = startUrl; }, [t, startUrl]);
  return (
    <div style={S.card} data-testid="onboard-intake-redirect">
      <div style={S.eyebrow}>Step 3 · Last part</div>
      <h2 style={S.h2}>Tell us about your business</h2>
      <p style={S.sub}>Takes about 3 minutes · we&rsquo;ll use this to build your ads &amp; landing page</p>
      <div style={S.pendingBox}>
        <div style={S.pendingSpinner} />
        <div>
          <div style={S.pendingTitle}>Redirecting in {t}s…</div>
          <div style={S.pendingSub}>Opens the form pre-filled with your email.</div>
        </div>
      </div>
      <a href={startUrl} style={S.primaryBtn} data-testid="onboard-open-intake-btn">Open the form now →</a>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// STEP · COMPLETE
// ═══════════════════════════════════════════════════════════════════════
function CompleteStep({ session }) {
  return (
    <div style={S.card} data-testid="onboard-complete">
      <div style={S.completeBadge}>✓</div>
      <h1 style={S.h1}>You&rsquo;re all set, {session.first_name || "friend"}</h1>
      <p style={S.sub}>Payment · kickoff booked · onboarding info received. Our team is on it.</p>
      <div style={S.summaryGrid}>
        <div style={S.summaryRow}><span>Payment</span><b>Paid ✓</b></div>
        {session.booking?.start_time && (
          <div style={S.summaryRow}>
            <span>Kickoff call</span>
            <b>{new Date(session.booking.start_time).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</b>
          </div>
        )}
        <div style={S.summaryRow}><span>Info form</span><b>Submitted ✓</b></div>
      </div>
      <p style={S.sub}>Check your email — we sent your kickoff-call link and a welcome pack. See you soon.</p>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// STYLES · mobile-first inline
// ═══════════════════════════════════════════════════════════════════════
const S = {
  page: { minHeight: "100vh", background: "#050508", color: "#fff", fontFamily: "'Manrope',system-ui,-apple-system,sans-serif", position: "relative", overflow: "hidden", paddingBottom: 40 },
  bgGlow: { position: "fixed", top: "-20%", left: "-10%", width: "60%", height: "60%", background: "radial-gradient(closest-side,rgba(157,50,243,.16),transparent)", pointerEvents: "none" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px", position: "relative", zIndex: 2 },
  logo: { height: 30 },
  badge: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 800, letterSpacing: 1.8, color: "#F472B6", background: "rgba(244,114,182,.08)", border: "1px solid rgba(244,114,182,.28)", padding: "5px 10px", borderRadius: 100 },
  main: { maxWidth: 480, margin: "0 auto", padding: "8px 16px 24px", position: "relative", zIndex: 2 },
  card: { background: "linear-gradient(180deg,rgba(255,255,255,.04),rgba(255,255,255,.02))", border: "1px solid rgba(255,255,255,.08)", borderRadius: 20, padding: "28px 22px 24px", boxShadow: "0 14px 40px -20px rgba(0,0,0,.5),inset 0 1px 0 rgba(255,255,255,.05)" },
  eyebrow: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 800, letterSpacing: 1.8, color: "rgba(255,255,255,.55)", textTransform: "uppercase", marginBottom: 10 },
  h1: { fontSize: 26, fontWeight: 800, lineHeight: 1.2, letterSpacing: "-.02em", margin: "0 0 8px", color: "#fff" },
  h2: { fontSize: 22, fontWeight: 800, lineHeight: 1.25, letterSpacing: "-.015em", margin: "0 0 8px", color: "#fff" },
  sub: { fontSize: 14, fontWeight: 400, color: "rgba(255,255,255,.62)", lineHeight: 1.5, margin: "0 0 18px" },
  field: { marginBottom: 12, display: "flex", flexDirection: "column", gap: 6 },
  label: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: "rgba(255,255,255,.55)", textTransform: "uppercase" },
  input: { padding: "13px 14px", fontSize: 15, background: "rgba(255,255,255,.04)", border: "1px solid rgba(255,255,255,.12)", borderRadius: 12, color: "#fff", outline: "none", fontFamily: "inherit", transition: "border-color .18s ease,background .18s ease" },
  row: { display: "flex", gap: 10 },
  primaryBtn: { display: "block", width: "100%", padding: "15px 20px", marginTop: 12, background: "linear-gradient(135deg,#dc328c 0%,#9d32f3 100%)", color: "#fff", fontSize: 15, fontWeight: 800, letterSpacing: "-.005em", border: "none", borderRadius: 14, cursor: "pointer", boxShadow: "0 10px 26px -6px rgba(220,50,140,.5),inset 0 1px 0 rgba(255,255,255,.2)", textAlign: "center", textDecoration: "none", fontFamily: "inherit" },
  secondaryBtn: { display: "block", width: "100%", padding: "13px 18px", marginTop: 10, background: "rgba(255,255,255,.05)", color: "rgba(255,255,255,.85)", fontSize: 14, fontWeight: 700, border: "1px solid rgba(255,255,255,.15)", borderRadius: 12, cursor: "pointer", textDecoration: "none", textAlign: "center", fontFamily: "inherit" },
  error: { padding: "10px 12px", background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.3)", borderRadius: 10, color: "#FCA5A5", fontSize: 13, margin: "8px 0" },
  info: { padding: "12px 14px", background: "rgba(90,178,255,.06)", border: "1px solid rgba(90,178,255,.22)", borderRadius: 10, color: "#93C5FD", fontSize: 13, margin: "8px 0" },
  trustRow: { display: "flex", justifyContent: "center", gap: 8, marginTop: 16, fontSize: 11, color: "rgba(255,255,255,.42)", flexWrap: "wrap" },
  loading: { minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#050508" },
  spinner: { width: 32, height: 32, border: "3px solid rgba(157,50,243,.25)", borderTopColor: "#dc328c", borderRadius: "50%", animation: "onbSpin 0.8s linear infinite" },
  footer: { textAlign: "center", fontSize: 11, color: "rgba(255,255,255,.35)", marginTop: 20, padding: "0 16px" },
  progress: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 6px 22px", position: "relative" },
  progressStep: { display: "flex", flexDirection: "column", alignItems: "center", flex: 1, position: "relative", gap: 6 },
  progressDot: { width: 42, height: 42, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, transition: "all .22s ease", zIndex: 2 },
  progressLabel: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" },
  progressLine: { position: "absolute", top: 20, left: "58%", right: "-42%", height: 2, zIndex: 1 },
  // Locked offer — minimal, one price, one line
  lockedOffer: { padding: "14px 16px", background: "linear-gradient(135deg,rgba(220,50,140,.14),rgba(157,50,243,.06))", border: "1.5px solid rgba(220,50,140,.35)", borderRadius: 14, marginBottom: 18, boxShadow: "0 0 24px -8px rgba(220,50,140,.45)" },
  lockedOfferLabel: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 800, letterSpacing: 1.6, color: "#F472B6", textTransform: "uppercase", marginBottom: 4 },
  lockedOfferPrice: { display: "flex", alignItems: "baseline", gap: 6, color: "#fff" },
  lockedOfferTotalRow: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, paddingTop: 10, borderTop: "1px dashed rgba(255,255,255,.14)" },
  lockedOfferTotalLabel: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 800, letterSpacing: 1.6, color: "rgba(255,255,255,.55)", textTransform: "uppercase" },
  lockedOfferTotalValue: { fontSize: 15, fontWeight: 800, color: "#fff", letterSpacing: "-.01em" },
  lockedOfferFinePrint: { fontSize: 11.5, color: "rgba(255,255,255,.5)", marginTop: 6, fontFamily: "'IBM Plex Mono',monospace", letterSpacing: .3 },
  inlineCode: { background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.12)", padding: "2px 6px", borderRadius: 6, fontFamily: "'IBM Plex Mono',monospace", fontSize: 12, color: "#F472B6" },
  // Flow preview — shows the 3-step sequence on step 1
  flowPreview: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 6, padding: "12px 12px", background: "rgba(255,255,255,.025)", border: "1px dashed rgba(255,255,255,.14)", borderRadius: 12, marginBottom: 16 },
  flowStep: { display: "flex", alignItems: "center", gap: 6, flex: "0 1 auto", minWidth: 0 },
  flowNum: { width: 20, height: 20, borderRadius: "50%", background: "rgba(255,255,255,.08)", color: "rgba(255,255,255,.55)", fontFamily: "'IBM Plex Mono',monospace", fontSize: 11, fontWeight: 800, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
  flowNumActive: { background: "linear-gradient(135deg,#dc328c,#9d32f3)", color: "#fff", boxShadow: "0 0 16px -2px rgba(220,50,140,.55)" },
  flowStepLabel: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, letterSpacing: .6, color: "rgba(255,255,255,.75)", textTransform: "uppercase", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  flowStepNow: { color: "#F472B6", marginLeft: 4, fontWeight: 800 },
  flowArrow: { color: "rgba(255,255,255,.28)", fontSize: 13, flexShrink: 0 },
  // Single-page card layout
  sectionLabel: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 800, letterSpacing: 1.6, color: "rgba(255,255,255,.55)", textTransform: "uppercase", marginTop: 12, marginBottom: 8, paddingBottom: 6, borderBottom: "1px solid rgba(255,255,255,.06)" },
  cardHint: { display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "rgba(255,255,255,.03)", border: "1px dashed rgba(255,255,255,.12)", borderRadius: 12, fontSize: 12.5, color: "rgba(255,255,255,.7)", marginTop: 4 },
  cardHintIcon: { fontSize: 16, flexShrink: 0 },
  embedBlock: { marginTop: 4 },
  embedTop: { marginBottom: 10 },
  embedTopTitle: { fontSize: 15, fontWeight: 800, color: "#fff", marginBottom: 2 },
  embedTopSub: { fontSize: 12, color: "rgba(255,255,255,.55)", fontFamily: "'IBM Plex Mono',monospace", letterSpacing: .3 },
  // Picker (fallback)
  pickerBlock: { marginBottom: 16 },
  pickerLabel: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: "rgba(255,255,255,.55)", textTransform: "uppercase", marginBottom: 8 },
  pickerRow: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8 },
  pickerBtn: { padding: "10px 6px", background: "rgba(255,255,255,.03)", border: "1.5px solid rgba(255,255,255,.1)", borderRadius: 12, color: "rgba(255,255,255,.7)", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, transition: "all .18s ease" },
  pickerBtnActive: { background: "linear-gradient(135deg,rgba(220,50,140,.18),rgba(157,50,243,.06))", borderColor: "rgba(220,50,140,.5)", color: "#fff", boxShadow: "0 0 20px -6px rgba(220,50,140,.4)" },
  pickerBtnTop: { fontSize: 11, fontWeight: 800 },
  pickerBtnMid: { fontSize: 14, fontWeight: 800, letterSpacing: "-.01em" },
  pickerBtnBot: { fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700, letterSpacing: .6, opacity: .6, textTransform: "uppercase" },
  // Whop inline embed
  embedWrap: { width: "100%", minHeight: 540, borderRadius: 14, overflow: "hidden", background: "#0a0a12", border: "1px solid rgba(255,255,255,.08)", boxShadow: "inset 0 1px 0 rgba(255,255,255,.03)", marginBottom: 12 },
  embedLoading: { display: "flex", alignItems: "center", justifyContent: "center", padding: 40, color: "rgba(255,255,255,.7)", fontSize: 13 },
  pendingHintRow: { display: "flex", alignItems: "center", gap: 8, fontSize: 11.5, color: "rgba(255,255,255,.5)", fontFamily: "'IBM Plex Mono',monospace", letterSpacing: .3, marginTop: 6, padding: "0 4px" },
  pendingSpinnerSmall: { width: 12, height: 12, border: "2px solid rgba(157,50,243,.25)", borderTopColor: "#dc328c", borderRadius: "50%", animation: "onbSpin 0.8s linear infinite", display: "inline-block" },
  // Pending payment (external)
  pendingBox: { display: "flex", alignItems: "center", gap: 14, padding: "16px 16px", background: "rgba(157,50,243,.06)", border: "1px solid rgba(157,50,243,.24)", borderRadius: 14, marginTop: 6 },
  pendingSpinner: { width: 22, height: 22, border: "2.5px solid rgba(157,50,243,.25)", borderTopColor: "#dc328c", borderRadius: "50%", flexShrink: 0, animation: "onbSpin 0.8s linear infinite" },
  pendingTitle: { fontSize: 14, fontWeight: 700, color: "#fff" },
  pendingSub: { fontSize: 12, color: "rgba(255,255,255,.55)", marginTop: 2 },
  // Calendar
  dateScroller: { display: "flex", gap: 8, overflowX: "auto", padding: "4px 2px 12px", marginLeft: -4, marginRight: -4, WebkitOverflowScrolling: "touch", scrollbarWidth: "none" },
  dateChip: { flexShrink: 0, minWidth: 68, padding: "10px 8px", background: "rgba(255,255,255,.03)", border: "1.5px solid rgba(255,255,255,.08)", borderRadius: 12, color: "rgba(255,255,255,.7)", cursor: "pointer", fontFamily: "inherit", display: "flex", flexDirection: "column", alignItems: "center", gap: 2, transition: "all .18s ease" },
  dateChipActive: { background: "linear-gradient(180deg,rgba(220,50,140,.14),rgba(157,50,243,.06))", borderColor: "rgba(220,50,140,.5)", color: "#fff" },
  dateChipDay: { fontSize: 10, fontFamily: "'IBM Plex Mono',monospace", fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase", opacity: .7 },
  dateChipDate: { fontSize: 15, fontWeight: 800 },
  dateChipCount: { fontSize: 9, fontFamily: "'IBM Plex Mono',monospace", fontWeight: 600, opacity: .5, letterSpacing: .4, marginTop: 2 },
  slotGrid: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 8, marginBottom: 14 },
  slotBtn: { padding: "12px 8px", background: "rgba(255,255,255,.03)", border: "1.5px solid rgba(255,255,255,.08)", borderRadius: 10, color: "rgba(255,255,255,.85)", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", transition: "all .18s ease" },
  slotBtnActive: { background: "linear-gradient(135deg,#dc328c,#9d32f3)", borderColor: "transparent", color: "#fff", boxShadow: "0 6px 18px -4px rgba(220,50,140,.55)" },
  // Complete
  completeBadge: { width: 60, height: 60, borderRadius: "50%", background: "linear-gradient(135deg,#4ADE80,#22c55e)", color: "#fff", fontSize: 30, fontWeight: 900, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px", boxShadow: "0 0 42px rgba(74,222,128,.4)" },
  summaryGrid: { display: "flex", flexDirection: "column", gap: 10, margin: "18px 0" },
  summaryRow: { display: "flex", justifyContent: "space-between", padding: "12px 14px", background: "rgba(255,255,255,.03)", border: "1px solid rgba(255,255,255,.06)", borderRadius: 10, fontSize: 13 },
};

if (typeof document !== "undefined" && !document.getElementById("onboard-kf")) {
  const s = document.createElement("style");
  s.id = "onboard-kf";
  s.textContent = "@keyframes onbSpin{to{transform:rotate(360deg)}}input::placeholder{color:rgba(255,255,255,.35)}input:focus{border-color:rgba(220,50,140,.55) !important;background:rgba(255,255,255,.06) !important}button:not(:disabled):hover{transform:translateY(-1px)}::-webkit-scrollbar{display:none}";
  document.head.appendChild(s);
}
